import { __decorate } from "tslib";
import { AutoEncoder, DateDecoder, EnumDecoder, MapDecoder, NumberDecoder, field } from '@simonbackx/simple-encoding';
import { Formatter } from '@stamhoofd/utility';
import { SetupStepReview } from './SetupStepReview.js';
import { SetupStepType } from './SetupStepType.js';
export const minimumRegistrationCount = 1;
export class SetupStep extends AutoEncoder {
    constructor() {
        super(...arguments);
        /**
         * @deprecated
         * Removed
         */
        this.__reviewedAt = null;
        /**
         * When the item was marked as reviewed.
         */
        this.review = null;
        /**
         * When the finished and total steps last have been updated.
         */
        this.updatedAt = new Date();
        this.finishedSteps = 0;
        this.totalSteps = 1;
    }
    transformForDiff() {
        if (this.isDone) {
            if (this.review) {
                return $t(`%nK`) + ' ' + Formatter.dateNumber(this.review.date);
            }
            return $t(`%Gt`);
        }
        return this.finishedSteps + '/' + this.totalSteps;
    }
    get isDone() {
        return this.finishedSteps >= this.totalSteps;
    }
    get isReviewed() {
        return this.review !== null;
    }
    get isComplete() {
        return this.isDone && this.isReviewed;
    }
    get progress() {
        if (!this.totalSteps)
            return 1;
        return this.finishedSteps / this.totalSteps;
    }
    get priority() {
        const isDone = this.isDone;
        const isReviewed = this.isReviewed;
        if (isDone && isReviewed) {
            return 0;
        }
        if (isDone && !isReviewed) {
            return 1;
        }
        if (!isDone && isReviewed) {
            return 2;
        }
        return 3;
    }
    markReviewed({ userId, userName }) {
        const now = new Date();
        if (this.review === null) {
            this.review = SetupStepReview.create({
                date: now,
                userName,
                userId,
            });
        }
    }
    resetReviewed() {
        this.review = null;
    }
    update(finishedSteps, totalSteps) {
        if (finishedSteps === this.finishedSteps && totalSteps === this.totalSteps) {
            return;
        }
        const now = new Date();
        if (totalSteps === 0) {
            finishedSteps = 1;
            totalSteps = 1;
        }
        this.finishedSteps = finishedSteps;
        this.totalSteps = totalSteps;
        this.updatedAt = now;
        if (this.finishedSteps < this.totalSteps && this.review) {
            this.resetReviewed();
        }
    }
}
__decorate([
    field({ decoder: DateDecoder, nullable: true, field: 'reviewedAt', optional: true })
], SetupStep.prototype, "__reviewedAt", void 0);
__decorate([
    field({ decoder: SetupStepReview, nullable: true, version: 326 })
], SetupStep.prototype, "review", void 0);
__decorate([
    field({ decoder: DateDecoder })
], SetupStep.prototype, "updatedAt", void 0);
__decorate([
    field({ decoder: NumberDecoder })
], SetupStep.prototype, "finishedSteps", void 0);
__decorate([
    field({ decoder: NumberDecoder })
], SetupStep.prototype, "totalSteps", void 0);
export class SetupSteps extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.steps = new Map();
    }
    get(type) {
        return this.steps.get(type);
    }
    getAll() {
        const result = [];
        for (const value of Object.values(SetupStepType)) {
            const step = this.steps.get(value);
            if (step) {
                result.push({ type: value, step });
            }
        }
        return result;
    }
    isEmpty() {
        return this.steps.size === 0;
    }
    areAllComplete() {
        return Array.from(this.steps.values()).every(s => s.isComplete);
    }
    getStepsToDoOverview() {
        const result = [];
        for (const value of Object.values(SetupStepType)) {
            const step = this.steps.get(value);
            if (step) {
                // filter out steps that are done and reviewed
                if (step.isDone && step.isReviewed) {
                    continue;
                }
                result.push({ type: value, step });
            }
        }
        return result;
    }
    getProgress() {
        const total = this.steps.size;
        const completed = Array.from(this.steps.values()).filter(s => s.isComplete).length;
        return { completed, total };
    }
    markReviewed(stepType, by) {
        const step = this.steps.get(stepType);
        if (step) {
            step.markReviewed(by);
        }
    }
    resetReviewed(stepType) {
        const step = this.steps.get(stepType);
        if (step) {
            step.resetReviewed();
        }
    }
    update(stepType, { finishedSteps, totalSteps }) {
        const step = this.steps.get(stepType);
        if (step) {
            step.update(finishedSteps, totalSteps);
        }
        else {
            if (totalSteps === 0) {
                this.steps.set(stepType, SetupStep.create({ finishedSteps: 1, totalSteps: 1 }));
            }
            else {
                this.steps.set(stepType, SetupStep.create({ finishedSteps, totalSteps }));
            }
        }
    }
    remove(stepType) {
        return this.steps.delete(stepType);
    }
}
__decorate([
    field({ decoder: new MapDecoder(new EnumDecoder(SetupStepType), SetupStep), version: 324 })
], SetupSteps.prototype, "steps", void 0);
//# sourceMappingURL=SetupSteps.js.map